import { useState } from 'react';
import { PRIMARY_BG } from '../../theme/brand';
import {  } from 'uuid';
import {
  Type,
  Image,
  Play,
  Mic,
  Layout
} from 'lucide-react';
import { useEditorStore, DEFAULT_TEXT_STYLE } from '../../store/editorStore';
import TextTemplatesPanel from './TextTemplatesPanel';
import MediaBrowser from './MediaBrowser';
import IconsPanel from './IconsPanel';
import { TEXT_TEMPLATES } from '../../data/textTemplates';

// ── Shape SVG Previews ────────────────────────────────────────

function ShapePreview({ shape, color = PRIMARY_BG }) {
  const style = { width: 28, height: 28 };

  switch (shape) {
    case 'rectangle':
      return <div style={{ ...style, background: color, borderRadius: 3 }} />;
    case 'rounded-rectangle':
      return <div style={{ ...style, background: color, borderRadius: 9 }} />;
    case 'circle':
      return <div style={{ ...style, background: color, borderRadius: '50%' }} />;
    case 'ellipse':
      return <div style={{ width: 34, height: 22, background: color, borderRadius: '50%' }} />;
    case 'line':
      return <div style={{ width: 32, height: 4, background: color, borderRadius: 2 }} />;
    case 'line-diagonal':
      return (
        <svg width="28" height="28" viewBox="0 0 28 28"><line x1="3" y1="25" x2="25" y2="3" stroke={color} strokeWidth="3.5" strokeLinecap="round" /></svg>
      );
    default:
      // SVG polygon/path shapes
      return (
        <svg width="30" height="30" viewBox="0 0 100 100" style={{ display: 'block' }}>
          {shapePath(shape, color)}
        </svg>
      );
  }
}

function shapePath(shape, color) {
  switch (shape) {
    case 'triangle':
      return <polygon points="50,8 96,90 4,90" fill={color} />;
    case 'triangle-down':
      return <polygon points="4,10 96,10 50,92" fill={color} />;
    case 'diamond':
      return <polygon points="50,5 95,50 50,95 5,50" fill={color} />;
    case 'star':
      return <polygon points="50,5 61,35 95,35 68,57 79,91 50,70 21,91 32,57 5,35 39,35" fill={color} />;
    case 'star6':
      return <polygon points="50,5 59,35 88,20 73,47 95,65 63,65 50,95 37,65 5,65 27,47 12,20 41,35" fill={color} />;
    case 'heart':
      return <path d="M50,88 C50,88 12,60 12,35 C12,22 22,15 35,15 C42,15 48,19 50,23 C52,19 58,15 65,15 C78,15 88,22 88,35 C88,60 50,88 50,88Z" fill={color} />;
    case 'pentagon':
      return <polygon points="50,5 97,38 79,95 21,95 3,38" fill={color} />;
    case 'hexagon':
      return <polygon points="50,5 93,27 93,73 50,95 7,73 7,27" fill={color} />;
    case 'octagon':
      return <polygon points="30,5 70,5 95,30 95,70 70,95 30,95 5,70 5,30" fill={color} />;
    case 'arrow-right':
      return <polygon points="0,25 65,25 65,10 100,50 65,90 65,75 0,75" fill={color} />;
    case 'arrow-left':
      return <polygon points="100,25 35,25 35,10 0,50 35,90 35,75 100,75" fill={color} />;
    case 'cross':
      return <polygon points="35,0 65,0 65,35 100,35 100,65 65,65 65,100 35,100 35,65 0,65 0,35 35,35" fill={color} />;
    case 'parallelogram':
      return <polygon points="25,10 100,10 75,90 0,90" fill={color} />;
    case 'trapezoid':
      return <polygon points="20,10 80,10 100,90 0,90" fill={color} />;
    // Social media logos
    case 'instagram':
      return <><rect x="15" y="15" width="70" height="70" rx="20" fill="none" stroke={color} strokeWidth="7"/><circle cx="50" cy="50" r="18" fill="none" stroke={color} strokeWidth="7"/><circle cx="73" cy="27" r="5" fill={color}/></>;
    case 'facebook':
      return <path d="M80,5H20C11.7,5,5,11.7,5,20v60c0,8.3,6.7,15,15,15h25V62H35V48h10V38c0-11,7.2-17,16.5-17c4.7,0,9.5,0.8,9.5,0.8v10.7H65.7c-5.3,0-7,3.3-7,6.7V48h12l-2,14H58.7v33H80c8.3,0,15-6.7,15-15V20C95,11.7,88.3,5,80,5z" fill={color} />;
    case 'twitter-x':
      return <path d="M87,10L53,52l36,38H78L51,59L29,90H5l36-42L7,10h12l24,27L64,10H87zM75,83L22,17h6l53,66H75z" fill={color} />;
    case 'youtube':
      return <><rect x="5" y="18" width="90" height="64" rx="16" fill={color}/><polygon points="40,35 40,65 68,50" fill="#fff"/></>;
    case 'tiktok':
      return <path d="M72,5v20c10,2,18-3,22-8v18c-6,3-14,2-22-3v30c0,18-15,33-33,33S6,80,6,62s15-33,33-33v18c-8,0-15,7-15,15s7,15,15,15s15-7,15-15V5H72z" fill={color} />;
    case 'linkedin':
      return <><rect x="5" y="5" width="90" height="90" rx="15" fill={color}/><rect x="18" y="42" width="14" height="40" fill="#fff"/><circle cx="25" cy="28" r="9" fill="#fff"/><path d="M44,42h13v5.5c2-3.5,7-6.5,14-6.5c13,0,16,8,16,20v21H74V64c0-5-1-10-8-10c-7,0-9,5-9,10v18H44V42z" fill="#fff"/></>;
    case 'whatsapp':
      return <path d="M50,5C25,5,5,25,5,50c0,8,2.2,15.5,5.8,22L5,95l23.5-5.5C35,92.5,42.3,95,50,95c25,0,45-20,45-45S75,5,50,5zM50,85c-7,0-13.5-2-19-5.5l-13,3.5l3.5-12.5C18,65,16,57.7,16,50c0-18.8,15.2-34,34-34s34,15.2,34,34S68.8,85,50,85zM70,58c-1.5-0.8-8-4-9.3-4.5c-1.3-0.5-2.2-0.8-3,0.8c-1,1.5-3.5,4.5-4.3,5.3c-0.8,1-1.5,1-3,0.3c-1.5-0.8-6.3-2.3-12-7.5c-4.5-4-7.5-9-8.3-10.5c-0.8-1.5,0-2.3,0.7-3c0.7-0.7,1.5-1.8,2.3-2.7c0.8-1,1-1.5,1.5-2.5c0.5-1,0.3-1.8,0-2.7c-0.3-0.8-3-7.3-4.2-10c-1-2.7-2.2-2.3-3-2.3H25c-1,0-2.7,0.3-4,1.8c-1.3,1.5-5,5-5,12s5.3,14,6,15c0.8,1,10.5,16,25.5,22.5c3.5,1.5,6.3,2.5,8.5,3.2c3.5,1,6.7,0.8,9.2,0.5c2.8-0.5,8-3.3,9.2-6.5c1-3.2,1-6,0.7-6.5C71.7,59,70.7,58.7,70,58z" fill={color} />;
    case 'pinterest':
      return <path d="M50,5C25,5,5,25,5,50c0,19,11.5,35.2,28,42c-0.5-3.5-0.8-8.8,0-12.5c1-4,6-25.5,6-25.5s-1.5-3-1.5-7.5c0-7,4-12.2,9-12.2c4.3,0,6.3,3.2,6.3,7c0,4.3-2.7,10.7-4.2,16.5c-1.2,5,2.5,9.2,7.5,9.2c9,0,16-9.5,16-23c0-12-8.7-20.5-21-20.5c-14.3,0-22.7,10.7-22.7,21.8c0,4.3,1.7,9,3.7,11.5c0.5,0.5,0.5,1,0.3,1.5c-0.3,1.5-1.2,5-1.3,5.5c-0.2,0.8-0.8,1-1.5,0.5C23,60,18.7,51.3,18.7,43c0-16,11.5-30.5,33.3-30.5c17.5,0,31,12.5,31,29C83,60,72,74,57,74c-5,0-9.7-2.5-11.3-5.5l-3.2,12c-1.2,4.5-4.2,10-6.3,13.3C40,95.2,45,96,50,96c25,0,45-20,45-45C95,26,75,5,50,5z" fill={color} />;
    // Decorative & design
    case 'badge':
      return <><circle cx="50" cy="45" r="35" fill={color}/><polygon points="32,72 50,95 68,72" fill={color}/></>;
    case 'ribbon':
      return <path d="M10,20h80v50c0,0-20-10-40,0s-40,0-40,0V20z M10,20l15,10L10,40 M90,20l-15,10L90,40" fill={color} />;
    case 'burst':
      return <polygon points="50,2 58,30 88,12 70,38 98,50 70,62 88,88 58,70 50,98 42,70 12,88 30,62 2,50 30,38 12,12 42,30" fill={color} />;
    case 'chat-bubble':
      return <path d="M10,10h80c0,0,0,0,0,0v50c0,0,0,0,0,0H35l-15,20v-20H10V10z" fill={color} rx="8" />;
    case 'speech-bubble':
      return <path d="M50,5C24,5,5,20,5,40c0,12,8,22,20,28l-5,22l20-15c3,0.5,6.5,0.8,10,0.8c26,0,45-15,45-35S76,5,50,5z" fill={color} />;
    case 'pill':
      return <rect x="5" y="30" width="90" height="40" rx="20" fill={color} />;
    case 'tag':
      return <path d="M10,10h45l35,40l-35,40H10V10z M30,40a7,7,0,1,0,0,0.1" fill={color} />;
    // Arrows & pointers
    case 'arrow-up':
      return <polygon points="50,5 90,55 70,55 70,95 30,95 30,55 10,55" fill={color} />;
    case 'arrow-down':
      return <polygon points="50,95 90,45 70,45 70,5 30,5 30,45 10,45" fill={color} />;
    case 'chevron-right':
      return <polyline points="30,10 70,50 30,90" fill="none" stroke={color} strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />;
    case 'chevron-left':
      return <polyline points="70,10 30,50 70,90" fill="none" stroke={color} strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />;
    // Utility
    case 'ring':
      return <circle cx="50" cy="50" r="38" fill="none" stroke={color} strokeWidth="10" />;
    case 'half-circle':
      return <path d="M10,50a40,40,0,0,1,80,0z" fill={color} />;
    case 'quarter-circle':
      return <path d="M10,90a80,80,0,0,1,80-80v80z" fill={color} />;
    case 'wave':
      return <path d="M0,50 C15,20 35,80 50,50 C65,20 85,80 100,50" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" />;
    case 'zigzag':
      return <polyline points="5,70 20,30 35,70 50,30 65,70 80,30 95,70" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />;
    // Extra creative shapes
    case 'flame':
      return <path d="M50,5 C50,5 75,30 75,55 C75,72 64,85 50,90 C36,85 25,72 25,55 C25,45 35,35 40,30 C40,30 38,50 50,55 C50,55 50,30 50,5Z" fill={color} />;
    case 'lightning':
      return <polygon points="55,5 25,50 45,50 40,95 75,42 52,42" fill={color} />;
    case 'location-pin':
      return <path d="M50,90 C50,90 18,55 18,35 C18,17 32,5 50,5 S82,17 82,35 C82,55 50,90 50,90Z M50,24 a12,12 0 1,0 0,24 a12,12 0 1,0 0,-24Z" fill={color} fillRule="evenodd" />;
    case 'crown':
      return <polygon points="10,75 10,35 30,50 50,15 70,50 90,35 90,75" fill={color} />;
    case 'checkmark':
      return <><circle cx="50" cy="50" r="45" fill={color} opacity="0.2"/><polyline points="28,50 44,66 74,36" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round"/></>;
    case 'x-mark':
      return <><circle cx="50" cy="50" r="45" fill={color} opacity="0.2"/><line x1="33" y1="33" x2="67" y2="67" stroke={color} strokeWidth="10" strokeLinecap="round"/><line x1="67" y1="33" x2="33" y2="67" stroke={color} strokeWidth="10" strokeLinecap="round"/></>;
    case 'music-note':
      return <path d="M70,10v55c0,10-9,18-20,18S30,75,30,65s9-15,20-15c4,0,7,1,10,3V25L40,30v-15L70,10z" fill={color} />;
    case 'swipe-up':
      return <><line x1="50" y1="85" x2="50" y2="30" stroke={color} strokeWidth="6" strokeLinecap="round"/><polyline points="30,50 50,25 70,50" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/><circle cx="50" cy="15" r="6" fill={color}/></>;
    case 'blob':
      return <path d="M45,10 C65,5 85,15 90,35 C95,55 85,75 70,85 C55,95 35,90 20,78 C5,66 5,45 15,30 C25,15 35,12 45,10Z" fill={color} />;
    case 'scalloped-circle':
      return <path d="M50,8 C55,15 62,10 67,15 C72,20 68,28 73,33 C78,38 87,36 90,43 C93,50 86,55 87,62 C88,69 96,74 95,81 C94,88 85,89 81,95 C77,95 72,90 65,91 C58,92 55,95 50,95 C45,95 42,92 35,91 C28,90 23,95 19,95 C15,89 6,88 5,81 C4,74 12,69 13,62 C14,55 7,50 10,43 C13,36 22,38 27,33 C32,28 28,20 33,15 C38,10 45,15 50,8Z" fill={color} />;
    case 'brace':
      return <path d="M35,5 C25,5 20,15 20,25 L20,40 C20,45 15,50 10,50 C15,50 20,55 20,60 L20,75 C20,85 25,95 35,95 M65,5 C75,5 80,15 80,25 L80,40 C80,45 85,50 90,50 C85,50 80,55 80,60 L80,75 C80,85 75,95 65,95" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" />;
    case 'snapchat':
      return <path d="M50,8c12,0,22,10,24,25c1,5,0,10,0,15c5,2,12,3,15,6c-5,3-12,4-14,8c-2,5,5,18-8,25c-8,4-12,3-17,5c-5-2-9-1-17-5c-13-7-6-20-8-25c-2-4-9-5-14-8c3-3,10-4,15-6c0-5-1-10,0-15C28,18,38,8,50,8z" fill={color} />;
    case 'spotify':
      return <><circle cx="50" cy="50" r="45" fill={color}/><path d="M30,38 C45,33 65,33 78,40" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round"/><path d="M33,52 C45,48 62,48 73,53" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round"/><path d="M37,65 C47,62 58,62 68,66" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round"/></>;
    case 'telegram':
      return <><circle cx="50" cy="50" r="45" fill={color}/><path d="M25,50 L72,32 L60,75 L47,58 L25,50Z" fill="#fff"/><path d="M47,58 L50,72 L55,58" fill="#d2e4f0"/><line x1="72" y1="32" x2="47" y2="58" stroke="#c8d8e5" strokeWidth="1"/></>;
    case 'shopping-bag':
      return <><rect x="18" y="35" width="64" height="55" rx="5" fill={color}/><path d="M33,40 V25 C33,15 40,8 50,8 S67,15 67,25 V40" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"/></>;
    case 'percent-badge':
      return <><circle cx="50" cy="50" r="42" fill={color}/><circle cx="38" cy="38" r="8" fill="none" stroke="#fff" strokeWidth="4"/><circle cx="62" cy="62" r="8" fill="none" stroke="#fff" strokeWidth="4"/><line x1="65" y1="33" x2="35" y2="67" stroke="#fff" strokeWidth="4" strokeLinecap="round"/></>;
    case 'price-sticker':
      return <><polygon points="50,5 58,20 75,12 70,30 90,32 78,45 92,58 75,60 78,78 60,70 52,88 42,72 25,80 28,62 8,58 22,45 10,32 28,30 25,12 42,20" fill={color}/><text x="50" y="55" textAnchor="middle" fill="#fff" fontSize="22" fontWeight="bold">$</text></>;
    default:
      return <rect x="5" y="5" width="90" height="90" rx="4" fill={color} />;
  }
}

// ── Element Definitions ───────────────────────────────────────

const TEXT_ELEMENTS = [
  {
    label: 'Headline',
    preview: <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--t-text)', lineHeight: 1 }}>Aa</span>,
    transform: { x: 5, y: 35, width: 90, height: 20 },
    create: () => ({
      type: 'text', content: 'Your Headline',
      style: { ...DEFAULT_TEXT_STYLE, fontSize: 48, fontWeight: '800', letterSpacing: -1 },
      preset: 'headline',
    }),
  },
  {
    label: 'Subheadline',
    preview: <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--t-text)', lineHeight: 1 }}>Aa</span>,
    transform: { x: 5, y: 58, width: 90, height: 10 },
    create: () => ({
      type: 'text', content: 'Your subheadline text',
      style: { ...DEFAULT_TEXT_STYLE, fontSize: 24, fontWeight: '500', color: '#d9d9d9' },
      preset: 'subheadline',
    }),
  },
  {
    label: 'Body Text',
    preview: <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--t-text)', lineHeight: 1 }}>Aa</span>,
    transform: { x: 5, y: 45, width: 90, height: 30 },
    create: () => ({
      type: 'text', content: 'Your body text goes here. Tell your story.',
      style: { ...DEFAULT_TEXT_STYLE, fontSize: 18, fontWeight: '400', lineHeight: 1.6, textAlign: 'left', color: '#cccccc' },
      preset: 'body',
    }),
  },
  {
    label: 'Caption',
    preview: <span style={{ fontSize: 9, fontWeight: 400, color: 'var(--t-muted)', lineHeight: 1 }}>Aa</span>,
    transform: { x: 5, y: 88, width: 90, height: 8 },
    create: () => ({
      type: 'text', content: 'Caption text',
      style: { ...DEFAULT_TEXT_STYLE, fontSize: 13, fontWeight: '400', color: '#999999' },
      preset: 'caption',
    }),
  },
  {
    label: 'Quote',
    preview: <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--t-text)', lineHeight: 1, fontStyle: 'italic' }}>"</span>,
    transform: { x: 5, y: 30, width: 90, height: 40 },
    create: () => ({
      type: 'text', content: '"Your inspiring quote goes here."',
      style: { ...DEFAULT_TEXT_STYLE, fontSize: 28, fontWeight: '700', fontStyle: 'italic', textAlign: 'center', color: '#f2f2f2' },
      preset: 'quote',
    }),
  },
  {
    label: 'Label',
    preview: (
      <div style={{ background: PRIMARY_BG, borderRadius: 4, padding: '2px 6px' }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', lineHeight: 1 }}>TAG</span>
      </div>
    ),
    transform: { x: 5, y: 5, width: 30, height: 7 },
    create: () => ({
      type: 'text', content: 'NEW',
      style: { ...DEFAULT_TEXT_STYLE, fontSize: 12, fontWeight: '800', letterSpacing: 2, color: '#ffffff', textTransform: 'uppercase' },
      preset: 'label',
    }),
  },
];

const MEDIA_ELEMENTS = [
  {
    label: 'Image',
    preview: <Image size={22} style={{ color: 'var(--t-muted)' }} />,
    transform: { x: 0, y: 0, width: 100, height: 100 },
    create: () => ({ type: 'image', mediaId: '', src: '', alt: '', objectFit: 'cover' }),
  },
  {
    label: 'Video',
    preview: <Play size={22} style={{ color: 'var(--t-muted)' }} />,
    transform: { x: 0, y: 0, width: 100, height: 100 },
    create: () => ({ type: 'video', mediaId: '', src: '', autoplay: true, loop: true, muted: true }),
  },
  {
    label: 'Audio',
    preview: <Mic size={22} style={{ color: 'var(--t-muted)' }} />,
    transform: { x: 5, y: 5, width: 20, height: 10 },
    create: () => ({ type: 'audio', mediaId: '', src: '', autoplay: true, loop: true, showIcon: true }),
  },
];

const SHAPE_ELEMENTS = [
  // ── Basic Shapes ──────────────────────────────────────────
  { label: 'Rectangle',    preview: <ShapePreview shape="rectangle" />,         transform: { x: 10, y: 20, width: 80, height: 35 }, create: () => ({ type: 'shape', shape: 'rectangle',         fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 4  }) },
  { label: 'Rounded Rect', preview: <ShapePreview shape="rounded-rectangle" />, transform: { x: 10, y: 20, width: 80, height: 35 }, create: () => ({ type: 'shape', shape: 'rounded-rectangle',  fill: '#8b5cf6', stroke: null, strokeWidth: 0, borderRadius: 24 }) },
  { label: 'Circle',       preview: <ShapePreview shape="circle" />,            transform: { x: 25, y: 25, width: 50, height: 50 }, create: () => ({ type: 'shape', shape: 'circle',            fill: '#ec4899', stroke: null, strokeWidth: 0, borderRadius: 50 }) },
  { label: 'Ellipse',      preview: <ShapePreview shape="ellipse" />,           transform: { x: 10, y: 30, width: 80, height: 40 }, create: () => ({ type: 'shape', shape: 'ellipse',           fill: '#f59e0b', stroke: null, strokeWidth: 0, borderRadius: 50 }) },
  { label: 'Triangle',     preview: <ShapePreview shape="triangle" />,          transform: { x: 20, y: 20, width: 60, height: 52 }, create: () => ({ type: 'shape', shape: 'triangle',          fill: '#f59e0b', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Tri Down',     preview: <ShapePreview shape="triangle-down" />,     transform: { x: 20, y: 20, width: 60, height: 52 }, create: () => ({ type: 'shape', shape: 'triangle-down',     fill: '#10b981', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Diamond',      preview: <ShapePreview shape="diamond" />,           transform: { x: 25, y: 15, width: 50, height: 70 }, create: () => ({ type: 'shape', shape: 'diamond',           fill: '#3b82f6', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Star',         preview: <ShapePreview shape="star" />,              transform: { x: 20, y: 15, width: 60, height: 60 }, create: () => ({ type: 'shape', shape: 'star',              fill: '#f59e0b', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Star 6pt',     preview: <ShapePreview shape="star6" />,             transform: { x: 20, y: 15, width: 60, height: 60 }, create: () => ({ type: 'shape', shape: 'star6',             fill: '#eab308', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Heart',        preview: <ShapePreview shape="heart" />,             transform: { x: 20, y: 20, width: 60, height: 60 }, create: () => ({ type: 'shape', shape: 'heart',             fill: '#ef4444', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Pentagon',     preview: <ShapePreview shape="pentagon" />,          transform: { x: 20, y: 15, width: 60, height: 65 }, create: () => ({ type: 'shape', shape: 'pentagon',          fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Hexagon',      preview: <ShapePreview shape="hexagon" />,           transform: { x: 20, y: 15, width: 60, height: 65 }, create: () => ({ type: 'shape', shape: 'hexagon',          fill: '#8b5cf6', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Octagon',      preview: <ShapePreview shape="octagon" />,           transform: { x: 20, y: 15, width: 60, height: 65 }, create: () => ({ type: 'shape', shape: 'octagon',          fill: '#0ea5e9', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Cross',        preview: <ShapePreview shape="cross" />,             transform: { x: 25, y: 20, width: 50, height: 60 }, create: () => ({ type: 'shape', shape: 'cross',            fill: '#ef4444', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Pill',         preview: <ShapePreview shape="pill" />,              transform: { x: 10, y: 40, width: 80, height: 20 }, create: () => ({ type: 'shape', shape: 'pill',              fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 50 }) },
  // ── Arrows & Pointers ─────────────────────────────────────
  { label: 'Arrow →',      preview: <ShapePreview shape="arrow-right" />,       transform: { x: 5,  y: 40, width: 90, height: 20 }, create: () => ({ type: 'shape', shape: 'arrow-right',      fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Arrow ←',      preview: <ShapePreview shape="arrow-left" />,        transform: { x: 5,  y: 40, width: 90, height: 20 }, create: () => ({ type: 'shape', shape: 'arrow-left',       fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Arrow ↑',      preview: <ShapePreview shape="arrow-up" />,          transform: { x: 35, y: 5,  width: 30, height: 90 }, create: () => ({ type: 'shape', shape: 'arrow-up',         fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Arrow ↓',      preview: <ShapePreview shape="arrow-down" />,        transform: { x: 35, y: 5,  width: 30, height: 90 }, create: () => ({ type: 'shape', shape: 'arrow-down',       fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Chevron ›',    preview: <ShapePreview shape="chevron-right" />,     transform: { x: 35, y: 20, width: 30, height: 60 }, create: () => ({ type: 'shape', shape: 'chevron-right',    fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Chevron ‹',    preview: <ShapePreview shape="chevron-left" />,      transform: { x: 35, y: 20, width: 30, height: 60 }, create: () => ({ type: 'shape', shape: 'chevron-left',     fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  // ── Decorative & Design ────────────────────────────────────
  { label: 'Badge',        preview: <ShapePreview shape="badge" />,             transform: { x: 25, y: 15, width: 50, height: 70 }, create: () => ({ type: 'shape', shape: 'badge',            fill: '#f59e0b', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Ribbon',       preview: <ShapePreview shape="ribbon" />,            transform: { x: 5,  y: 5,  width: 90, height: 20 }, create: () => ({ type: 'shape', shape: 'ribbon',           fill: '#ef4444', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Burst',        preview: <ShapePreview shape="burst" />,             transform: { x: 15, y: 15, width: 70, height: 70 }, create: () => ({ type: 'shape', shape: 'burst',            fill: '#f59e0b', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Chat',         preview: <ShapePreview shape="chat-bubble" />,       transform: { x: 5,  y: 10, width: 90, height: 50 }, create: () => ({ type: 'shape', shape: 'chat-bubble',      fill: '#3b82f6', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Speech',       preview: <ShapePreview shape="speech-bubble" />,     transform: { x: 5,  y: 10, width: 90, height: 55 }, create: () => ({ type: 'shape', shape: 'speech-bubble',    fill: '#10b981', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Tag',          preview: <ShapePreview shape="tag" />,               transform: { x: 5,  y: 35, width: 60, height: 30 }, create: () => ({ type: 'shape', shape: 'tag',              fill: '#8b5cf6', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Parallelogram',preview: <ShapePreview shape="parallelogram" />,     transform: { x: 5,  y: 30, width: 90, height: 40 }, create: () => ({ type: 'shape', shape: 'parallelogram',    fill: '#0ea5e9', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Trapezoid',    preview: <ShapePreview shape="trapezoid" />,         transform: { x: 5,  y: 30, width: 90, height: 40 }, create: () => ({ type: 'shape', shape: 'trapezoid',        fill: '#10b981', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  // ── Utility ────────────────────────────────────────────────
  { label: 'Ring',          preview: <ShapePreview shape="ring" />,             transform: { x: 20, y: 20, width: 60, height: 60 }, create: () => ({ type: 'shape', shape: 'ring',             fill: '#ec4899', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Half Circle',   preview: <ShapePreview shape="half-circle" />,      transform: { x: 15, y: 25, width: 70, height: 35 }, create: () => ({ type: 'shape', shape: 'half-circle',      fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Quarter',       preview: <ShapePreview shape="quarter-circle" />,   transform: { x: 5,  y: 5,  width: 60, height: 60 }, create: () => ({ type: 'shape', shape: 'quarter-circle',   fill: '#8b5cf6', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Wave',          preview: <ShapePreview shape="wave" />,             transform: { x: 0,  y: 40, width: 100, height: 20}, create: () => ({ type: 'shape', shape: 'wave',             fill: '#3b82f6', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Zigzag',        preview: <ShapePreview shape="zigzag" />,           transform: { x: 0,  y: 40, width: 100, height: 20}, create: () => ({ type: 'shape', shape: 'zigzag',           fill: '#f59e0b', stroke: null, strokeWidth: 0, borderRadius: 0  }) },
  { label: 'Line H',        preview: <ShapePreview shape="line" />,             transform: { x: 5,  y: 48, width: 90, height: 1  }, create: () => ({ type: 'shape', shape: 'line',             fill: 'rgba(255,255,255,0.5)', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Line D',        preview: <ShapePreview shape="line-diagonal" />,    transform: { x: 5,  y: 5,  width: 90, height: 90 }, create: () => ({ type: 'shape', shape: 'line-diagonal',   fill: 'rgba(255,255,255,0.5)', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  // ── Stickers & Symbols ──────────────────────────────────────
  { label: 'Flame',       preview: <ShapePreview shape="flame" color="#ff6b35" />,        transform: { x: 30, y: 15, width: 40, height: 70 }, create: () => ({ type: 'shape', shape: 'flame',          fill: '#ff6b35', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Lightning',   preview: <ShapePreview shape="lightning" color="#fbbf24" />,    transform: { x: 30, y: 10, width: 40, height: 80 }, create: () => ({ type: 'shape', shape: 'lightning',      fill: '#fbbf24', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Location',    preview: <ShapePreview shape="location-pin" color="#ef4444" />, transform: { x: 30, y: 10, width: 40, height: 70 }, create: () => ({ type: 'shape', shape: 'location-pin',   fill: '#ef4444', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Crown',       preview: <ShapePreview shape="crown" color="#f59e0b" />,        transform: { x: 15, y: 25, width: 70, height: 40 }, create: () => ({ type: 'shape', shape: 'crown',          fill: '#f59e0b', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Checkmark',   preview: <ShapePreview shape="checkmark" color="#10b981" />,    transform: { x: 25, y: 25, width: 50, height: 50 }, create: () => ({ type: 'shape', shape: 'checkmark',      fill: '#10b981', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'X Mark',      preview: <ShapePreview shape="x-mark" color="#ef4444" />,       transform: { x: 25, y: 25, width: 50, height: 50 }, create: () => ({ type: 'shape', shape: 'x-mark',         fill: '#ef4444', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Music Note',  preview: <ShapePreview shape="music-note" color="#8b5cf6" />,   transform: { x: 25, y: 15, width: 50, height: 70 }, create: () => ({ type: 'shape', shape: 'music-note',     fill: '#8b5cf6', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Swipe Up',    preview: <ShapePreview shape="swipe-up" color={PRIMARY_BG} />,     transform: { x: 35, y: 10, width: 30, height: 80 }, create: () => ({ type: 'shape', shape: 'swipe-up',       fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Blob',        preview: <ShapePreview shape="blob" color="#ec4899" />,         transform: { x: 15, y: 15, width: 70, height: 70 }, create: () => ({ type: 'shape', shape: 'blob',           fill: '#ec4899', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Scalloped',   preview: <ShapePreview shape="scalloped-circle" color={PRIMARY_BG} />, transform: { x: 20, y: 20, width: 60, height: 60 }, create: () => ({ type: 'shape', shape: 'scalloped-circle', fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Brace { }',   preview: <ShapePreview shape="brace" color="#94a3b8" />,        transform: { x: 10, y: 10, width: 80, height: 80 }, create: () => ({ type: 'shape', shape: 'brace',          fill: '#94a3b8', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  // ── Shopping & Promo ───────────────────────────────────────
  { label: 'Shop Bag',    preview: <ShapePreview shape="shopping-bag" color={PRIMARY_BG} />,  transform: { x: 25, y: 15, width: 50, height: 70 }, create: () => ({ type: 'shape', shape: 'shopping-bag',   fill: PRIMARY_BG, stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: '% Badge',     preview: <ShapePreview shape="percent-badge" color="#ef4444" />, transform: { x: 20, y: 20, width: 60, height: 60 }, create: () => ({ type: 'shape', shape: 'percent-badge',  fill: '#ef4444', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Price Tag',   preview: <ShapePreview shape="price-sticker" color="#f59e0b" />, transform: { x: 20, y: 20, width: 60, height: 60 }, create: () => ({ type: 'shape', shape: 'price-sticker',  fill: '#f59e0b', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  // ── Social Media Logos ─────────────────────────────────────
  { label: 'Instagram',  preview: <ShapePreview shape="instagram"  color="#E4405F" />, transform: { x: 30, y: 30, width: 40, height: 40 }, create: () => ({ type: 'shape', shape: 'instagram',  fill: '#E4405F', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Facebook',   preview: <ShapePreview shape="facebook"   color="#1877F2" />, transform: { x: 30, y: 30, width: 40, height: 40 }, create: () => ({ type: 'shape', shape: 'facebook',   fill: '#1877F2', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'X / Twitter', preview: <ShapePreview shape="twitter-x" color="#000000" />, transform: { x: 30, y: 30, width: 40, height: 40 }, create: () => ({ type: 'shape', shape: 'twitter-x',  fill: '#000000', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'YouTube',    preview: <ShapePreview shape="youtube"    color="#FF0000" />, transform: { x: 25, y: 30, width: 50, height: 35 }, create: () => ({ type: 'shape', shape: 'youtube',    fill: '#FF0000', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'TikTok',     preview: <ShapePreview shape="tiktok"     color="#000000" />, transform: { x: 30, y: 25, width: 40, height: 50 }, create: () => ({ type: 'shape', shape: 'tiktok',     fill: '#000000', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'LinkedIn',   preview: <ShapePreview shape="linkedin"   color="#0A66C2" />, transform: { x: 30, y: 30, width: 40, height: 40 }, create: () => ({ type: 'shape', shape: 'linkedin',   fill: '#0A66C2', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'WhatsApp',   preview: <ShapePreview shape="whatsapp"   color="#25D366" />, transform: { x: 30, y: 30, width: 40, height: 40 }, create: () => ({ type: 'shape', shape: 'whatsapp',   fill: '#25D366', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Pinterest',  preview: <ShapePreview shape="pinterest"  color="#E60023" />, transform: { x: 30, y: 30, width: 40, height: 40 }, create: () => ({ type: 'shape', shape: 'pinterest',  fill: '#E60023', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Snapchat',  preview: <ShapePreview shape="snapchat"  color="#FFFC00" />, transform: { x: 30, y: 30, width: 40, height: 40 }, create: () => ({ type: 'shape', shape: 'snapchat',  fill: '#FFFC00', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Spotify',   preview: <ShapePreview shape="spotify"   color="#1DB954" />, transform: { x: 30, y: 30, width: 40, height: 40 }, create: () => ({ type: 'shape', shape: 'spotify',   fill: '#1DB954', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
  { label: 'Telegram',  preview: <ShapePreview shape="telegram"  color="#26A5E4" />, transform: { x: 30, y: 30, width: 40, height: 40 }, create: () => ({ type: 'shape', shape: 'telegram',  fill: '#26A5E4', stroke: null, strokeWidth: 0, borderRadius: 0 }) },
];

const UI_ELEMENTS = [
  {
    label: 'CTA Button',
    preview: (
      <div style={{ background: PRIMARY_BG, borderRadius: 20, padding: '4px 10px' }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: '#fff' }}>Button</span>
      </div>
    ),
    transform: { x: 10, y: 78, width: 80, height: 10 },
    create: () => ({
      type: 'cta', text: 'Learn More', url: 'https://example.com', openInNewTab: true,
      style: {
        backgroundColor: PRIMARY_BG, color: '#ffffff', fontSize: 16,
        fontWeight: '700', borderRadius: 50, padding: [12, 28, 12, 28], border: null,
      },
    }),
  },
  {
    label: 'Overlay',
    preview: (
      <div style={{ width: 28, height: 28, background: 'rgba(0,0,0,0.4)', borderRadius: 3, border: '1px dashed rgba(255,255,255,0.3)' }} />
    ),
    transform: { x: 0, y: 0, width: 100, height: 100 },
    create: () => ({ type: 'overlay', fill: 'rgba(0,0,0,0.4)' }),
  },
];

// ── Tabs config ───────────────────────────────────────────────

const TABS = [
  {
    id: 'Text',
    icon: <Type size={15} />,
    label: 'Text',
    elements: TEXT_ELEMENTS,
    cols: 2,
  },
  // {
  //   id: 'Media',
  //   icon: <Image size={15} />,
  //   label: 'Media',
  //   elements: MEDIA_ELEMENTS,
  //   cols: 3,
  // },
  {
    id: 'Shapes',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="8" height="8" rx="1" />
        <circle cx="18" cy="6" r="4" />
        <polygon points="12,22 22,22 17,14" />
        <rect x="2" y="14" width="8" height="8" rx="4" />
      </svg>
    ),
    label: 'Shapes',
    elements: SHAPE_ELEMENTS,
    cols: 3,
  },
  {
    id: 'Icons',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M8 14s1.5 2 4 2 4-2 4-2" />
        <line x1="9" y1="9" x2="9.01" y2="9" />
        <line x1="15" y1="9" x2="15.01" y2="9" />
      </svg>
    ),
    label: 'Icons',
    elements: [],  // handled by custom panel
    cols: 4,
  },
  {
    id: 'UI',
    icon: <Layout size={15} />,
    label: 'UI',
    elements: UI_ELEMENTS,
    cols: 2,
  },
];

// ── Component ─────────────────────────────────────────────────

export default function ElementsPanel() {
  const [activeTab, setActiveTab] = useState('Text');
  const activePageId   = useEditorStore((s) => s.activePageId);
  const story          = useEditorStore((s) => s.story);
  const addLayer       = useEditorStore((s) => s.addLayer);
  const setActiveSidebarTab = useEditorStore((s) => s.setActiveSidebarTab);

  const tab = TABS.find((t) => t.id === activeTab);

  function handleAdd(el) {
    if (!activePageId || !story) return;
    if (el.label === 'Image' || el.label === 'Video') {
      setActiveSidebarTab('media');
      return;
    }
    addLayer(activePageId, el.create(), { ...(el.transform || {}), rotation: 0, opacity: 1 });
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tab Bar */}
      <div
        className="flex border-b shrink-0"
        style={{ borderColor: 'var(--t-border)', background: 'var(--t-surface)' }}
      >
        {TABS.map((t) => {
          const isActive = t.id === activeTab;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className="flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors"
              style={{
                color: isActive ? PRIMARY_BG : 'var(--t-muted)',
                borderBottom: isActive ? '2px solid var(--brand-primary)' : '2px solid transparent',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <span style={{ color: isActive ? PRIMARY_BG : 'var(--t-muted)' }}>{t.icon}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-2">
        <div
          className="grid gap-1.5"
          style={{ gridTemplateColumns: `repeat(${tab.cols}, 1fr)` }}
        >
          {tab.elements.map((el) => (
            <button
              key={el.label}
              onClick={() => handleAdd(el)}
              className="flex flex-col items-center justify-center gap-1.5 rounded-lg border transition-all"
              style={{
                padding: '10px 4px 8px',
                background: 'var(--t-panel)',
                borderColor: 'var(--t-border)',
                cursor: 'pointer',
                minHeight: 64,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--t-panel2)';
                e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--t-panel)';
                e.currentTarget.style.borderColor = 'var(--t-border)';
              }}
            >
              <div className="flex items-center justify-center" style={{ height: 32 }}>
                {el.preview}
              </div>
              <span
                className="text-center leading-tight"
                style={{ fontSize: 10, color: 'var(--t-muted)', maxWidth: '100%', wordBreak: 'break-word' }}
              >
                {el.label}
              </span>
            </button>
          ))}
        </div>

        {/* Text Templates section — only shown on Text tab */}
        {activeTab === 'Text' && (
          <div className="mt-3">
            <div
              className="flex items-center gap-2 mb-2"
              style={{ borderTop: '1px solid var(--t-border)', paddingTop: 10 }}
            >
              <span className="text-xs font-semibold" style={{ color: 'var(--t-text)' }}>Templates</span>
              <span className="text-[10px]" style={{ color: 'var(--t-muted)' }}>{TEXT_TEMPLATES.length}</span>
            </div>
            <TextTemplatesPanel />
          </div>
        )}

        {/* Media Browser — only shown on Media tab */}
        {activeTab === 'Media' && (
          <div className="mt-3" style={{ borderTop: '1px solid var(--t-border)', marginLeft: -8, marginRight: -8 }}>
            <MediaBrowser />
          </div>
        )}

        {/* Icons Panel — only shown on Icons tab */}
        {activeTab === 'Icons' && (
          <div style={{ marginLeft: -8, marginRight: -8, marginTop: -8, height: 'calc(100% + 8px)' }}>
            <IconsPanel />
          </div>
        )}

      </div>
    </div>
  );
}
